import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Tag, Modal, Form, Input, Select, DatePicker, message, Space, Tabs, Descriptions, InputNumber, Popconfirm, List, Typography, Row, Col, Grid } from 'antd';
import { PlusOutlined, EnvironmentOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, HistoryOutlined, FormOutlined } from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

const { Option } = Select;
const { TabPane } = Tabs;
const { Title } = Typography;

const FarmsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isStaff = user?.role === 'STAFF';
    const [farms, setFarms] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [partners, setPartners] = useState([]);
    const [branches, setBranches] = useState([]);
    const [locations, setLocations] = useState([]);
    const [harvestActivities, setHarvestActivities] = useState([]);
    const [harvestBranchId, setHarvestBranchId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isHarvestModalVisible, setIsHarvestModalVisible] = useState(false);

    const [isFarmModalVisible, setIsFarmModalVisible] = useState(false);
    const [isCycleModalVisible, setIsCycleModalVisible] = useState(false);
    const [isCycleEventModalVisible, setIsCycleEventModalVisible] = useState(false);
    const [isCycleHistoryModalVisible, setIsCycleHistoryModalVisible] = useState(false);
    const [isFarmEventModalVisible, setIsFarmEventModalVisible] = useState(false);
    const [isFarmHistoryModalVisible, setIsFarmHistoryModalVisible] = useState(false);
    const [editingFarm, setEditingFarm] = useState(null);
    const [editingCycle, setEditingCycle] = useState(null);
    const [selectedCycle, setSelectedCycle] = useState(null);
    const [selectedFarm, setSelectedFarm] = useState(null);
    const [cycleEvents, setCycleEvents] = useState([]);
    const [farmEvents, setFarmEvents] = useState([]);
    const [farmCycles, setFarmCycles] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const [farmForm] = Form.useForm();
    const [cycleForm] = Form.useForm();
    const [harvestForm] = Form.useForm();
    const [cycleEventForm] = Form.useForm();
    const [farmEventForm] = Form.useForm();

    useEffect(() => {
        fetchFarms();
        fetchCycles();
        fetchPartners();
        fetchItems();
        fetchCategories();
        fetchBranches();
        fetchLocations();
        fetchHarvestActivities();
    }, []);

    useEffect(() => {
        if (branches.length && !harvestBranchId) {
            const defaultBranch = branches[0];
            setHarvestBranchId(defaultBranch.id);
            harvestForm.setFieldsValue((prev) => ({
                ...prev,
                branch_id: defaultBranch.id,
            }));
        }
    }, [branches, harvestBranchId, harvestForm]);

    useEffect(() => {
        if (harvestBranchId && locations.length) {
            const first = locations.find((loc) => loc.branch_id === harvestBranchId);
            if (first && harvestForm.getFieldValue('location_id') !== first.id) {
                harvestForm.setFieldsValue((prev) => ({
                    ...prev,
                    location_id: first.id,
                }));
            }
        }
    }, [harvestBranchId, locations, harvestForm]);

    const fetchCategories = async () => {
        try {
            const { data } = await client.get('/master/categories');
            setCategories(data.filter(c => ['Palay', 'Rice', 'Vegetables', 'Corn', 'Crops'].includes(c.name)));
        } catch (error) {
            console.error('Failed to load categories');
        }
    };

    const fetchItems = async () => {
        try {
            const { data } = await client.get('/master/items');
            setItems(data.filter(i => ['Palay', 'Rice', 'Vegetables', 'Corn', 'Crops'].includes(i.category?.name)));
        } catch (error) {
            console.error('Failed to load items');
        }
    };

    const fetchBranches = async () => {
        try {
            const { data } = await client.get('/master/branches');
            setBranches(data);
        } catch (error) {
            console.error('Failed to load branches');
        }
    };

    const fetchLocations = async () => {
        try {
            const { data } = await client.get('/master/locations');
            setLocations(data);
        } catch (error) {
            console.error('Failed to load locations');
        }
    };

    const fetchHarvestActivities = async () => {
        try {
            const { data } = await client.get('/transactions/crop-cycles/activities');
            setHarvestActivities(data.filter((activity) => activity.activity_type === 'Harvest'));
        } catch (error) {
            console.error('Failed to load harvest history');
        }
    };

    const fetchFarms = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/transactions/farms');
            setFarms(data);
        } catch (error) {
            message.error('Failed to load farms');
        } finally {
            setLoading(false);
        }
    };

    const fetchCycles = async () => {
        try {
            const { data } = await client.get('/transactions/crop-cycles');
            setCycles(data);
        } catch (error) {
            message.error('Failed to load crop cycles');
        }
    };

    const fetchPartners = async () => {
        try {
            const { data } = await client.get('/master/partners');
            setPartners(data.filter(p => p.type === 'FARMER' || p.type === 'BOTH'));
        } catch (error) {
            console.error('Failed to load partners');
        }
    };

    const handleSubmitFarm = async (values) => {
        try {
            if (editingFarm) {
                await client.put(`/transactions/farms/${editingFarm.id}`, values);
                message.success('Farm updated successfully');
            } else {
                await client.post('/transactions/farms', values);
                message.success('Farm registered successfully');
            }
            setIsFarmModalVisible(false);
            setEditingFarm(null);
            farmForm.resetFields();
            fetchFarms();
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.message || 'Failed to save farm';
            message.error(errorMessage);
        }
    };

    const handleDeleteFarm = async (id) => {
        try {
            await client.delete(`/transactions/farms/${id}`);
            message.success('Farm deleted successfully');
            fetchFarms();
        } catch (error) {
            message.error('Failed to delete farm');
        }
    };

    const handleSubmitCycle = async (values) => {
        try {
            const payload = {
                ...values,
                start_date: values.start_date.format('YYYY-MM-DD'),
                estimated_harvest_date: values.estimated_harvest_date?.format('YYYY-MM-DD'),
            };

            if (editingCycle) {
                await client.put(`/transactions/crop-cycles/${editingCycle.id}`, payload);
                message.success('Crop cycle updated successfully');
            } else {
                await client.post('/transactions/crop-cycles', payload);
                message.success('Crop cycle started successfully');
            }
            setIsCycleModalVisible(false);
            setEditingCycle(null);
            cycleForm.resetFields();
            fetchCycles();
        } catch (error) {
            message.error('Failed to save crop cycle');
        }
    };

    const handleHarvestSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                actual_harvest_date: values.actual_harvest_date.format('YYYY-MM-DD'),
                branch_id: values.branch_id,
                location_id: values.location_id,
                notes: values.notes,
            };
            await client.post(`/transactions/crop-cycles/${editingCycle.id}/harvest`, payload);
            message.success('Harvest recorded successfully');
            setIsHarvestModalVisible(false);
            setEditingCycle(null);
            fetchCycles();
            fetchFarms();
            fetchHarvestActivities();
            harvestForm.resetFields();
        } catch (error) {
            message.error('Failed to record harvest');
        }
    };

    const handleDeleteCycle = async (id) => {
        try {
            await client.delete(`/transactions/crop-cycles/${id}`);
            message.success('Crop cycle deleted successfully');
            fetchCycles();
        } catch (error) {
            message.error('Failed to delete crop cycle');
        }
    };

    const openFarmModal = (farm = null) => {
        setEditingFarm(farm);
        if (farm) {
            farmForm.setFieldsValue(farm);
        } else {
            farmForm.resetFields();
        }
        setIsFarmModalVisible(true);
    };

    const openCycleModal = (cycle = null) => {
        setEditingCycle(cycle);
        if (cycle) {
            cycleForm.setFieldsValue({
                ...cycle,
                start_date: dayjs(cycle.start_date),
                estimated_harvest_date: cycle.estimated_harvest_date ? dayjs(cycle.estimated_harvest_date) : null,
            });
        } else {
            cycleForm.resetFields();
            cycleForm.setFieldsValue({ start_date: dayjs() });
        }
        setIsCycleModalVisible(true);
    };

    const openHarvestModal = (cycle) => {
        setEditingCycle(cycle);
        harvestForm.resetFields();
        harvestForm.setFieldsValue({
            actual_harvest_date: dayjs(),
            branch_id: harvestBranchId || (branches[0]?.id ?? null),
        });
        setIsHarvestModalVisible(true);
    };

    const openCycleEventModal = (cycle) => {
        setSelectedCycle(cycle);
        cycleEventForm.resetFields();
        cycleEventForm.setFieldsValue({ activity_date: dayjs() });
        setIsCycleEventModalVisible(true);
    };

    const handleRecordCycleEvent = async (values) => {
        try {
            await client.post(`/transactions/crop-cycles/${selectedCycle.id}/activities`, {
                activity_type: values.activity_type,
                activity_date: values.activity_date.format('YYYY-MM-DD'),
                notes: values.notes,
            });
            message.success('Event recorded successfully');
            setIsCycleEventModalVisible(false);
            cycleEventForm.resetFields();
            fetchHarvestActivities();
        } catch (error) {
            message.error('Failed to record event');
        }
    };

    const viewCycleHistory = async (cycle) => {
        setSelectedCycle(cycle);
        setHistoryLoading(true);
        try {
            const { data } = await client.get(`/transactions/crop-cycles/${cycle.id}`);
            const ordered = (data.activities || []).slice().sort((a, b) => dayjs(b.activity_date).valueOf() - dayjs(a.activity_date).valueOf());
            setCycleEvents(ordered);
            setIsCycleHistoryModalVisible(true);
        } catch (error) {
            message.error('Failed to load history');
        } finally {
            setHistoryLoading(false);
        }
    };

    const openFarmEventModal = async (farm) => {
        setSelectedFarm(farm);
        setFarmCycles([]);
        farmEventForm.resetFields();
        farmEventForm.setFieldsValue({ activity_date: dayjs() });
        setIsFarmEventModalVisible(true);
        try {
            const { data } = await client.get(`/transactions/farms/${farm.id}`);
            const cyclesForFarm = data.cropCycles || [];
            setFarmCycles(cyclesForFarm);
            if (cyclesForFarm.length === 1) {
                farmEventForm.setFieldsValue({ crop_cycle_id: cyclesForFarm[0].id });
            }
        } catch (error) {
            message.error('Failed to load crop cycles for farm');
        }
    };

    const handleRecordFarmEvent = async (values) => {
        try {
            await client.post(`/transactions/crop-cycles/${values.crop_cycle_id}/activities`, {
                activity_type: values.activity_type,
                activity_date: values.activity_date.format('YYYY-MM-DD'),
                notes: values.notes,
            });
            message.success('Event recorded successfully');
            setIsFarmEventModalVisible(false);
            farmEventForm.resetFields();
            fetchHarvestActivities();
        } catch (error) {
            message.error('Failed to record event');
        }
    };

    const viewFarmHistory = async (farm) => {
        setSelectedFarm(farm);
        setHistoryLoading(true);
        try {
            const { data } = await client.get(`/transactions/farms/${farm.id}/activities`);
            const ordered = (data || []).slice().sort((a, b) => dayjs(b.activity_date).valueOf() - dayjs(a.activity_date).valueOf());
            setFarmEvents(ordered);
            setIsFarmHistoryModalVisible(true);
        } catch (error) {
            message.error('Failed to load history');
        } finally {
            setHistoryLoading(false);
        }
    };

    const farmColumns = [
        { title: 'Farm Name', dataIndex: 'name', key: 'name' },
        { title: 'Owner', key: 'owner', render: (_, record) => record.owner?.name || record.owner_name || '-' },
        { title: 'Location', dataIndex: 'location_address', key: 'location_address', render: (text) => <span><EnvironmentOutlined /> {text}</span> },
        { title: 'Size (Ha)', dataIndex: 'size_hectares', key: 'size_hectares' },
        { title: 'Status', dataIndex: 'is_active', key: 'is_active', render: (active) => <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag> },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => openFarmModal(record)} />
                    {!isStaff && (
                        <Popconfirm title="Delete?" onConfirm={() => handleDeleteFarm(record.id)}>
                            <Button icon={<DeleteOutlined />} size="small" danger />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    const cycleColumns = [
        { title: 'Control #', dataIndex: 'control_number', key: 'control_number' },
        { title: 'Farm', dataIndex: ['farm', 'name'], key: 'farm_name' },
        { title: 'Crop', dataIndex: 'crop_type', key: 'crop_type' },
        { title: 'Variety', dataIndex: 'variety', key: 'variety' },
        { title: 'Start Date', dataIndex: 'start_date', key: 'start_date', render: (date) => dayjs(date).format('MMM D, YYYY') },
        { title: 'Est. Harvest', dataIndex: 'estimated_harvest_date', key: 'estimated_harvest_date', render: (date) => date ? dayjs(date).format('MMM D, YYYY') : '-' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = { PLANTED: 'blue', GROWING: 'cyan', HARVESTING: 'orange', COMPLETED: 'green', FAILED: 'red' };
                return <Tag color={colors[status]}>{status}</Tag>;
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<FormOutlined />} size="small" onClick={() => openCycleEventModal(record)}>
                        Event
                    </Button>
                    <Button icon={<HistoryOutlined />} size="small" onClick={() => viewCycleHistory(record)}>
                        History
                    </Button>
                    <Button icon={<EditOutlined />} size="small" onClick={() => openCycleModal(record)} />
                    {record.status !== 'COMPLETED' && (
                        <Button
                            icon={<CheckCircleOutlined />}
                            size="small"
                            type="primary"
                            ghost
                            onClick={() => openHarvestModal(record)}
                        >
                            Harvest
                        </Button>
                    )}
                    {!isStaff && (
                        <Popconfirm title="Delete?" onConfirm={() => handleDeleteCycle(record.id)}>
                            <Button icon={<DeleteOutlined />} size="small" danger />
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    const activityColumns = [
        {
            title: 'Date',
            dataIndex: 'activity_date',
            key: 'activity_date',
            render: (date) => date ? dayjs(date).format('MMM D, YYYY') : '-',
        },
        {
            title: 'Cycle',
            dataIndex: ['cropCycle', 'control_number'],
            key: 'cycle',
            render: (value) => value || '-',
        },
        {
            title: 'Farm',
            dataIndex: ['cropCycle', 'farm', 'name'],
            key: 'farm',
            render: (value) => value || '-',
        },
        { title: 'Crop', dataIndex: ['cropCycle', 'crop_type'], key: 'crop', render: (value) => value || '-' },
        { title: 'Variety', dataIndex: ['cropCycle', 'variety'], key: 'variety', render: (value) => value || '-' },
        {
            title: 'Qty (kg)',
            dataIndex: ['cropCycle', 'harvested_quantity'],
            key: 'quantity',
            render: (value) => (value ? Number(value).toLocaleString() : '-'),
        },
        {
            title: 'Item',
            dataIndex: ['cropCycle', 'harvestedItem', 'name'],
            key: 'item',
            render: (value) => value || '-',
        },
        {
            title: 'Notes',
            dataIndex: 'notes',
            key: 'notes',
            render: (value) => value || '-',
        },
    ];

    const branchOptions = branches.map((branch) => ({
        label: branch.name,
        value: branch.id,
    }));

    const filteredHarvestLocations = harvestBranchId
        ? locations.filter((loc) => loc.branch_id === harvestBranchId)
        : locations;

    const locationOptions = filteredHarvestLocations.map((location) => ({
        label: location.name,
        value: location.id,
    }));

    const handleHarvestBranchChange = (branchId) => {
        setHarvestBranchId(branchId);
        harvestForm.setFieldsValue((prev) => ({
            ...prev,
            branch_id: branchId,
        }));
        const fallbackLocation = locations.find((loc) => loc.branch_id === branchId);
        if (fallbackLocation) {
            harvestForm.setFieldsValue((prev) => ({
                ...prev,
                location_id: fallbackLocation.id,
            }));
        }
    };

    const filteredHarvestItems = items.filter((item) => {
        if (!editingCycle?.crop_type) {
            return true;
        }
        const cropType = String(editingCycle.crop_type).toLowerCase();
        const categoryName = String(item.category?.name || '').toLowerCase();
        if (cropType.includes('vegetable')) {
            return categoryName.includes('vegetable');
        }
        return categoryName === cropType;
    });

    const StatCard = ({ title, value, prefix, suffix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{parseFloat(value).toLocaleString()}{suffix}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const activeCyclesCount = cycles.filter(c => ['PLANTED', 'GROWING', 'HARVESTING'].includes(c.status)).length;
    const totalYieldKg = harvestActivities.reduce((sum, a) => sum + Number(a.cropCycle?.harvested_quantity || 0), 0);

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Farm Monitoring</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage crop cycles, harvest tracking, and farm registries.</Typography.Text>
                </div>
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Farms"
                        value={farms.length}
                        icon={<i className="fa-solid fa-map-location-dot"></i>}
                        color="#3b82f6"
                        subText="Registered fields"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Active Cycles"
                        value={activeCyclesCount}
                        icon={<i className="fa-solid fa-seedling"></i>}
                        color="#10b981"
                        subText="Currently growing crops"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Cumulative Yield"
                        value={totalYieldKg}
                        suffix=" kg"
                        icon={<i className="fa-solid fa-wheat-awn"></i>}
                        color="#f59e0b"
                        subText="Total harvested volume"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: '0 24px 24px 24px' }}>
                <Tabs defaultActiveKey="1" size="large" animated={{ inkBar: true, tabPane: true }}>
                    <TabPane
                        tab={<span><i className="fa-solid fa-clock-rotate-left" style={{ marginRight: 8 }}></i>Active Crop Cycles</span>}
                        key="1"
                    >
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24, marginTop: 16 }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => openCycleModal(null)}
                                style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                            >
                                Start Crop Cycle
                            </Button>
                        </div>
                        <Table
                            dataSource={cycles}
                            columns={cycleColumns}
                            rowKey="id"
                            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                        />

                        <div style={{ marginTop: 48, marginBottom: 16 }}>
                            <Title level={4} style={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                                <i className="fa-solid fa-clipboard-list" style={{ color: '#10b981', marginRight: 12 }}></i>
                                Harvest History
                            </Title>
                        </div>
                        <Table
                            columns={activityColumns}
                            dataSource={harvestActivities}
                            rowKey="id"
                            pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] }}
                            size="middle"
                        />
                    </TabPane>
                    <TabPane
                        tab={<span><i className="fa-solid fa-list-check" style={{ marginRight: 8 }}></i>Farm Registry</span>}
                        key="2"
                    >
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24, marginTop: 16 }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => openFarmModal(null)}
                                style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                            >
                                Register Farm
                            </Button>
                        </div>
                        <Table dataSource={farms} columns={farmColumns} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} />
                    </TabPane>
                </Tabs>
            </Card>

            <Modal
                title={editingFarm ? "Edit Farm" : "Register Farm"}
                open={isFarmModalVisible}
                onCancel={() => setIsFarmModalVisible(false)}
                footer={null}
            >
                <Form form={farmForm} layout="vertical" onFinish={handleSubmitFarm}>
                    <Form.Item name="owner_name" label="Owner Name">
                        <Input placeholder="Enter owner name" />
                    </Form.Item>
                    <Form.Item name="name" label="Farm Name (e.g. North Field)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="location_address" label="Location / Address">
                        <Input />
                    </Form.Item>
                    <Form.Item name="size_hectares" label="Size (Hectares)">
                        <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>{editingFarm ? "Update Farm" : "Register Farm"}</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={editingCycle ? "Edit Crop Cycle" : "Start Crop Cycle"}
                open={isCycleModalVisible}
                onCancel={() => setIsCycleModalVisible(false)}
                footer={null}
            >
                <Form form={cycleForm} layout="vertical" onFinish={handleSubmitCycle}>
                    <Form.Item name="farm_id" label="Farm" rules={[{ required: true }]}>
                        <Select showSearch optionFilterProp="children">
                            {farms.filter((farm) => farm?.is_active).map((farm) => (
                                <Option key={farm.id} value={farm.id}>
                                    {farm.name} ({farm.owner?.name || farm.owner_name || '-'})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="crop_type" label="Crop Type" rules={[{ required: true }]}>
                        <Select placeholder="Select crop type">
                            {categories.map(cat => (
                                <Option key={cat.id} value={cat.name}>{cat.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="variety" label="Variety">
                        <Input placeholder="e.g. RC216" />
                    </Form.Item>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <Form.Item name="start_date" label="Planting Date" initialValue={dayjs()} rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name="estimated_harvest_date" label="Est. Harvest">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </div>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>{editingCycle ? "Update Cycle" : "Start Cycle"}</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Record Harvest"
                open={isHarvestModalVisible}
                onCancel={() => setIsHarvestModalVisible(false)}
                footer={null}
            >
                <Form form={harvestForm} layout="vertical" onFinish={handleHarvestSubmit}>
                    <Descriptions column={1} className="mb-4">
                        <Descriptions.Item label="Cycle">{editingCycle?.control_number}</Descriptions.Item>
                        <Descriptions.Item label="Crop">{editingCycle?.crop_type} ({editingCycle?.variety})</Descriptions.Item>
                    </Descriptions>
                    <Form.Item name="actual_harvest_date" label="Harvest Date" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="harvested_quantity" label="Harvested Quantity (kg)" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="0.00" />
                    </Form.Item>
                    <Form.Item name="harvested_item_id" label="Store to Inventory Item" rules={[{ required: true }]}>
                        <Select placeholder="Select item to update stock" showSearch optionFilterProp="children">
                            {filteredHarvestItems.map(i => (
                                <Option key={i.id} value={i.id}>{i.name} ({i.code})</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="branch_id" label="Branch" rules={[{ required: true }]}>
                        <Select
                            placeholder="Select branch responsible for storage"
                            options={branchOptions}
                            onChange={handleHarvestBranchChange}
                        />
                    </Form.Item>
                    <Form.Item name="location_id" label="Location" rules={[{ required: true }]}>
                        <Select
                            placeholder="Select storage location"
                            options={locationOptions}
                            showSearch
                            filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={2} placeholder="Optional notes (storage, yield, etc.)" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>Complete Harvest</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Record Cycle Event - ${selectedCycle?.control_number || ''}`}
                open={isCycleEventModalVisible}
                onCancel={() => setIsCycleEventModalVisible(false)}
                footer={null}
            >
                <Form form={cycleEventForm} layout="vertical" onFinish={handleRecordCycleEvent}>
                    <Descriptions column={1} className="mb-4">
                        <Descriptions.Item label="Farm">{selectedCycle?.farm?.name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Crop">{selectedCycle?.crop_type} ({selectedCycle?.variety || '-'})</Descriptions.Item>
                    </Descriptions>
                    <Form.Item name="activity_type" label="Event Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="Planting">Planting</Option>
                            <Option value="Fertilizing">Fertilizing</Option>
                            <Option value="Irrigation">Irrigation</Option>
                            <Option value="Spraying">Spraying</Option>
                            <Option value="Weeding">Weeding</Option>
                            <Option value="Pest Control">Pest Control</Option>
                            <Option value="Note">General Note</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="activity_date" label="Date" initialValue={dayjs()} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3} placeholder="Optional details" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block>Record Event</Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Cycle History - ${selectedCycle?.control_number || ''}`}
                open={isCycleHistoryModalVisible}
                onCancel={() => setIsCycleHistoryModalVisible(false)}
                footer={null}
                width={600}
            >
                <List
                    itemLayout="horizontal"
                    dataSource={cycleEvents}
                    loading={historyLoading}
                    renderItem={(item) => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<Tag color="blue">{item.activity_type}</Tag>}
                                title={`${dayjs(item.activity_date).format('MMM D, YYYY')} - ${item.notes || 'No description'}`}
                            />
                        </List.Item>
                    )}
                />
            </Modal>

            <Modal
                title={`Record Farm Event - ${selectedFarm?.name || ''}`}
                open={isFarmEventModalVisible}
                onCancel={() => setIsFarmEventModalVisible(false)}
                footer={null}
            >
                <Form form={farmEventForm} layout="vertical" onFinish={handleRecordFarmEvent}>
                    <Form.Item name="crop_cycle_id" label="Crop Cycle" rules={[{ required: true }]}>
                        <Select placeholder="Select crop cycle">
                            {farmCycles.map((cycle) => (
                                <Option key={cycle.id} value={cycle.id}>
                                    {cycle.control_number} - {cycle.crop_type} {cycle.variety ? `(${cycle.variety})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {farmCycles.length === 0 && (
                        <Typography.Text type="secondary">No crop cycles found for this farm.</Typography.Text>
                    )}
                    <Form.Item name="activity_type" label="Event Type" rules={[{ required: true }]}>
                        <Select>
                            <Option value="Planting">Planting</Option>
                            <Option value="Fertilizing">Fertilizing</Option>
                            <Option value="Irrigation">Irrigation</Option>
                            <Option value="Spraying">Spraying</Option>
                            <Option value="Weeding">Weeding</Option>
                            <Option value="Pest Control">Pest Control</Option>
                            <Option value="Note">General Note</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="activity_date" label="Date" initialValue={dayjs()} rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3} placeholder="Optional details" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block disabled={farmCycles.length === 0}>
                            Record Event
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Farm History - ${selectedFarm?.name || ''}`}
                open={isFarmHistoryModalVisible}
                onCancel={() => setIsFarmHistoryModalVisible(false)}
                footer={null}
                width={650}
            >
                <List
                    itemLayout="horizontal"
                    dataSource={farmEvents}
                    loading={historyLoading}
                    renderItem={(item) => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<Tag color="blue">{item.activity_type}</Tag>}
                                title={`${dayjs(item.activity_date).format('MMM D, YYYY')} - ${item.notes || 'No description'}`}
                                description={`Cycle: ${item.cropCycle?.control_number || '-'} | Crop: ${item.cropCycle?.crop_type || '-'}`}
                            />
                        </List.Item>
                    )}
                />
            </Modal>
        </div>
    );
};

export default FarmsPage;




